<script lang="ts">
  import {
    createUnit as apiCreateUnit,
    cloneUnit as apiCloneUnit,
    deleteUnit,
    getErrorMessage,
    patchUnit
  } from '$lib/api';
  import { createQuery } from '@tanstack/svelte-query';
  import {
    Plus,
    Building2,
    Gamepad2,
    Activity,
    Grid,
    FolderOpen,
    Download,
    ChevronDown,
    Trash2,
    Eye,
    Copy,
    Pencil,
    ChevronRight,
    Search
  } from 'lucide-svelte';
  import { router } from '$lib/router';
  import { setLoading, setError, clearError, uiState } from '$lib/state/ui.svelte';
  import { showSuccess, showError } from '$lib/state/toast.svelte';
  import { projectsQuery } from '$lib/queries/projects';
  import { unitsQuery } from '$lib/queries/units';
  import { fetchZones } from '$lib/api/zone';
  import { fetchRooms } from '$lib/api/room';
  import { fetchLoads } from '$lib/api/load';
  import { fetchInputDevices } from '$lib/api/input_device';
  import { invalidateAfterUnitUpdate } from '$lib/state/invalidation';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import Modal from './ui/Modal.svelte';
  import SkeletonCard from './ui/SkeletonCard.svelte';
  import FilesPanel from './FilesPanel.svelte';
  import {
    downloadProjectBOMXLSX,
    downloadProjectBOQCSV,
    downloadProjectBOQJSON,
    sendProjectToProQuote
  } from '../projectExports';
  import type { Unit } from '$lib/types/unit';

  let {
    openUnit = (_u: Unit) => {}
  }: {
    openUnit?: (unit: Unit) => void;
  } = $props();

  const projectsQ = projectsQuery();
  const unitsQ = unitsQuery();

  const zonesQ = createQuery(() => ({
    queryKey: ['zones', 'all'],
    queryFn: fetchZones
  }));
  const roomsQ = createQuery(() => ({
    queryKey: ['rooms', 'all'],
    queryFn: fetchRooms
  }));
  const loadsQ = createQuery(() => ({
    queryKey: ['loads', 'all'],
    queryFn: fetchLoads
  }));
  const inputDevicesQ = createQuery(() => ({
    queryKey: ['input_devices', 'all'],
    queryFn: () => fetchInputDevices()
  }));

  let projectId = $derived(router.projectId ?? 0);
  let project = $derived((projectsQ.data ?? []).find((p) => p.id === projectId) ?? null);
  let units = $derived(unitsQ.data ?? []);
  let isUnitsLoading = $derived(unitsQ.isLoading);
  let allZones = $derived(zonesQ.data ?? []);
  let allRooms = $derived(roomsQ.data ?? []);
  let allLoads = $derived(loadsQ.data ?? []);
  let allInputDevices = $derived(inputDevicesQ.data ?? []);

  let showCreateUnit = $state(false);
  let newUnit = $state({ name: '', notes: '' });
  let searchTerm = $state('');
  let activeTab = $state('units');

  let showDeleteUnitConfirm = $state(false);
  let unitToDelete = $state<number | null>(null);

  let showCloneUnit = $state(false);
  let unitToClone = $state<Unit | null>(null);
  let cloneName = $state('');
  let cloning = $state(false);

  let editingUnitId = $state<number | null>(null);
  let editingName = $state('');
  let renamingUnitId = $state<number | null>(null);

  let showExportMenu = $state(false);
  let sendingToQuote = $state(false);
  let proQuotePanel = $state<{
    unresolved: string[];
    created_products: string[];
    warning: string | null;
  } | null>(null);

  // Create new unit
  async function createUnit() {
    if (!newUnit.name.trim() || !projectId) return;

    setLoading(true);
    clearError();

    try {
      await apiCreateUnit({
        name: newUnit.name,
        description: '',
        notes: newUnit.notes,
        project: projectId
      });
      invalidateAfterUnitUpdate(projectId);
      newUnit = { name: '', notes: '' };
      showCreateUnit = false;
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  // Delete unit
  function confirmDeleteUnit(unitId: number) {
    unitToDelete = unitId;
    showDeleteUnitConfirm = true;
  }

  async function handleDeleteUnit() {
    showDeleteUnitConfirm = false;
    if (!unitToDelete) return;

    setLoading(true);
    clearError();

    try {
      await deleteUnit(unitToDelete);
      invalidateAfterUnitUpdate(projectId);
      showSuccess('Unit deleted successfully');
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setLoading(false);
      unitToDelete = null;
    }
  }

  function focusOnMount(node: HTMLInputElement) {
    node.focus();
    node.select();
  }

  // Clone unit
  function confirmCloneUnit(unit: Unit) {
    unitToClone = unit;
    cloneName = `${unit.name} (copy)`;
    showCloneUnit = true;
  }

  async function handleCloneUnit() {
    if (!unitToClone || !cloneName.trim()) return;

    cloning = true;
    clearError();
    try {
      await apiCloneUnit(unitToClone.id, cloneName.trim());
      invalidateAfterUnitUpdate(projectId);
      showSuccess('Unit cloned successfully');
      showCloneUnit = false;
      unitToClone = null;
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      cloning = false;
    }
  }

  // Inline rename
  function startEditingUnitName(unit: Unit) {
    editingUnitId = unit.id;
    editingName = unit.name;
  }

  function cancelEditingUnitName() {
    editingUnitId = null;
    editingName = '';
  }

  async function saveUnitName(unit: Unit) {
    const name = editingName.trim();
    editingUnitId = null;
    if (!name || name === unit.name) return;

    renamingUnitId = unit.id;
    try {
      await patchUnit(unit.id, { name });
      invalidateAfterUnitUpdate(projectId);
    } catch (error) {
      showError('Rename failed: ' + getErrorMessage(error));
    } finally {
      renamingUnitId = null;
    }
  }

  // Get unit stats
  function getUnitStats(unitId: number) {
    const unitZones = allZones.filter((z) => z.unit === unitId);
    const unitRooms = allRooms.filter((r) => unitZones.some((z) => z.id === r.zone));
    const unitLoads = allLoads.filter((l) => unitRooms.some((r) => r.id === l.room));
    const unitKeypads = allInputDevices.filter((k) =>
      unitRooms.some((r) => r.id === k.location_room)
    );
    return {
      zones: unitZones.length,
      rooms: unitRooms.length,
      loads: unitLoads.length,
      keypads: unitKeypads.length
    };
  }

  // Get unit status
  function getUnitStatus(unitId: number) {
    const stats = getUnitStats(unitId);

    if (stats.zones === 0 && stats.rooms === 0) return 'setup';
    if (stats.loads === 0) return 'planning';
    if (stats.keypads === 0) return 'keypads';
    return 'complete';
  }

  async function exportBOM() {
    await downloadProjectBOMXLSX();
  }

  async function exportBOQCSV() {
    showExportMenu = false;
    await downloadProjectBOQCSV();
  }

  async function exportBOQJSON() {
    showExportMenu = false;
    await downloadProjectBOQJSON();
  }

  async function handleSendToProQuote() {
    showExportMenu = false;
    sendingToQuote = true;
    proQuotePanel = null;
    try {
      const { panel } = await sendProjectToProQuote();
      proQuotePanel = panel;
    } catch {
      // error toast handled in projectExports
    } finally {
      sendingToQuote = false;
    }
  }

  // Filter units
  let filteredUnits = $derived(
    units.filter((unit) => {
      const matchesSearch =
        unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (unit.notes && unit.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (unit.description && unit.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch;
    })
  );

  // Status colors and labels
  const statusConfig = {
    setup: {
      label: 'Setup',
      color: 'bg-bg-hover text-text-primary',
      icon: Building2
    },
    planning: {
      label: 'Planning',
      color: 'bg-bg-hover text-text-secondary',
      icon: Building2
    },
    keypads: {
      label: 'Keypads',
      color: 'bg-bg-hover text-text-secondary',
      icon: Gamepad2
    },
    complete: {
      label: 'Complete',
      color: 'bg-bg-hover text-text-secondary',
      icon: Activity
    }
  };
</script>

<div class="p-6">
  <!-- Header -->
  <div class="mb-8">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <!-- Title + Subtitle -->
      <div>
        <h1 class="text-2xl font-bold text-text-primary">
          {project?.name ?? 'Project'}
        </h1>
        <p class="text-text-secondary mt-1">Manage units and project files</p>
      </div>

      <!-- Actions -->
      <div class="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
        <div class="relative">
          <button
            class="bg-bg-canvas border border-border-strong flex flex-row gap-2 justify-between items-center font-medium text-center hover:bg-bg-hover text-text-primary text-sm px-3 py-2 rounded-md transition-colors"
            onclick={(e) => {
              e.stopPropagation();
              showExportMenu = !showExportMenu;
            }}
            disabled={uiState.isLoading || sendingToQuote}
          >
            Export / Quote
            <ChevronDown class="w-4 h-4" />
          </button>
          {#if showExportMenu}
            <div
              class="absolute right-0 top-full mt-1 w-52 bg-bg-canvas border border-border-strong rounded shadow-toyama-md z-50 py-1 pop-in"
              onclick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
                onclick={() => {
                  showExportMenu = false;
                  exportBOM();
                }}
              >
                <Download class="w-3.5 h-3.5" />
                BOM (XLSX)
              </button>
              <button
                type="button"
                class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
                onclick={exportBOQCSV}
              >
                <Download class="w-3.5 h-3.5" />
                BOQ (CSV)
              </button>
              <button
                type="button"
                class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-text-primary hover:bg-bg-hover text-left"
                onclick={exportBOQJSON}
              >
                <Download class="w-3.5 h-3.5" />
                BOQ (JSON)
              </button>
              <div class="my-1 border-t border-border-default"></div>
              <button
                type="button"
                class="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-accent-on-primary bg-accent-primary/90 hover:bg-accent-primary text-left font-medium"
                onclick={handleSendToProQuote}
                disabled={sendingToQuote}
              >
                Send to ProQuote
              </button>
            </div>
          {/if}
        </div>
        {#if activeTab === 'units'}
          <button
            onclick={() => (showCreateUnit = true)}
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-accent-on-primary bg-accent-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary"
          >
            <Plus class="w-4 h-4 mr-2" />
            New Unit
          </button>
        {/if}
      </div>
    </div>
  </div>

  <!-- Tab Navigation -->
  <div class="bg-bg-canvas rounded-lg shadow-sm border border-border-default mb-6">
    <div class="border-b border-border-default">
      <nav class="flex space-x-8 px-6">
        <button
          onclick={() => (activeTab = 'units')}
          class="flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 {activeTab ===
          'units'
            ? 'border-accent-primary text-text-primary'
            : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-strong'}"
        >
          <Grid class="w-4 h-4" />
          <span>Units</span>
        </button>
        <button
          onclick={() => (activeTab = 'files')}
          class="flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 {activeTab ===
          'files'
            ? 'border-accent-primary text-text-primary'
            : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-strong'}"
        >
          <FolderOpen class="w-4 h-4" />
          <span>Files</span>
        </button>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="p-6">
      {#if activeTab === 'units'}
        <!-- Search -->
        <div class="mb-6">
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="flex-1">
              <div class="relative">
                <Search
                  class="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4"
                />
                <input
                  type="text"
                  bind:value={searchTerm}
                  placeholder="Search units..."
                  class="w-full pl-10 pr-4 py-2 border border-border-strong rounded-md focus:ring-accent-primary focus:border-accent-primary"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Units Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {#if isUnitsLoading}
            {#each Array(6) as _}
              <SkeletonCard />
            {/each}
          {:else}
            {#each filteredUnits as unit}
              {@const stats = getUnitStats(unit.id)}
              {@const status = getUnitStatus(unit.id)}
              {@const statusInfo = statusConfig[status]}
              {@const StatusIcon = statusInfo.icon}

              <div
                class="bg-bg-canvas rounded-lg shadow-sm border border-border-default hover:shadow-md transition-shadow duration-200"
              >
                <div class="p-6">
                  <!-- Unit Header -->
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex-1 min-w-0">
                      {#if editingUnitId === unit.id}
                        <input
                          type="text"
                          bind:value={editingName}
                          use:focusOnMount
                          onblur={() => saveUnitName(unit)}
                          onkeydown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditingUnitName();
                            }
                          }}
                          class="w-full text-lg font-semibold text-text-primary mb-1 border border-accent-primary rounded px-1 -mx-1 focus:outline-none focus:ring-2 focus:ring-accent-primary"
                        />
                      {:else}
                        <button
                          type="button"
                          onclick={() => startEditingUnitName(unit)}
                          class="group flex items-center gap-1.5 text-lg font-semibold text-text-primary mb-1 text-left hover:text-accent-primary"
                          title="Click to rename"
                        >
                          <span class="truncate">{unit.name}</span>
                          {#if renamingUnitId === unit.id}
                            <span class="text-xs text-text-muted">Saving…</span>
                          {:else}
                            <Pencil
                              class="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 shrink-0"
                            />
                          {/if}
                        </button>
                      {/if}
                      {#if unit.notes || unit.description}
                        <p class="text-sm text-text-secondary line-clamp-2">
                          {unit.notes || unit.description}
                        </p>
                      {/if}
                    </div>
                    <div class="flex items-center space-x-2 ml-4">
                      <button
                        onclick={() => openUnit(unit)}
                        class="p-1 text-text-muted hover:text-text-secondary rounded"
                        title="Open Unit"
                        aria-label="View unit"
                      >
                        <Eye class="w-4 h-4" />
                      </button>
                      <button
                        onclick={() => confirmCloneUnit(unit)}
                        class="p-1 text-text-muted hover:text-text-secondary rounded"
                        title="Clone Unit"
                        aria-label="Clone unit"
                      >
                        <Copy class="w-4 h-4" />
                      </button>
                      <button
                        onclick={() => confirmDeleteUnit(unit.id)}
                        class="p-1 text-text-muted hover:text-state-error rounded"
                        title="Delete Unit"
                        aria-label="Delete unit"
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <!-- Status Badge -->
                  <div class="flex items-center mb-4">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {statusInfo.color}"
                    >
                      <StatusIcon class="w-3 h-3 mr-1" />
                      {statusInfo.label}
                    </span>
                  </div>

                  <!-- Stats -->
                  <div class="grid grid-cols-2 gap-4 mb-4">
                    <div class="text-center">
                      <p class="text-2xl font-bold text-text-primary">
                        {stats.zones}
                      </p>
                      <p class="text-xs text-text-muted">Zones</p>
                    </div>
                    <div class="text-center">
                      <p class="text-2xl font-bold text-text-primary">
                        {stats.rooms}
                      </p>
                      <p class="text-xs text-text-muted">Rooms</p>
                    </div>
                    <div class="text-center">
                      <p class="text-2xl font-bold text-text-primary">
                        {stats.loads}
                      </p>
                      <p class="text-xs text-text-muted">Loads</p>
                    </div>
                    <div class="text-center">
                      <p class="text-2xl font-bold text-text-primary">
                        {stats.keypads}
                      </p>
                      <p class="text-xs text-text-muted">Devices</p>
                    </div>
                  </div>

                  <!-- Action Button -->
                  <button
                    onclick={() => openUnit(unit)}
                    class="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-bg-base bg-text-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-opacity"
                  >
                    Open Unit
                    <ChevronRight class="ml-2 w-4 h-4" />
                  </button>
                </div>
              </div>
            {/each}
          {/if}
        </div>

        <!-- Empty State for Units -->
        {#if filteredUnits.length === 0 && units.length > 0}
          <div class="text-center py-12">
            <Building2 class="mx-auto h-12 w-12 text-text-muted" />
            <h3 class="mt-2 text-sm font-medium text-text-primary">No units found</h3>
            <p class="mt-1 text-sm text-text-muted">Try adjusting your search criteria.</p>
          </div>
        {/if}

        {#if units.length === 0}
          <div class="text-center py-12">
            <Building2 class="mx-auto h-12 w-12 text-text-muted" />
            <h3 class="mt-2 text-sm font-medium text-text-primary">No units yet</h3>
            <p class="mt-1 text-sm text-text-muted">Get started by creating your first unit.</p>
            <div class="mt-6">
              <button
                onclick={() => (showCreateUnit = true)}
                class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-accent-on-primary bg-accent-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary"
              >
                <Plus class="w-4 h-4 mr-2" />
                Create Unit
              </button>
            </div>
          </div>
        {/if}
      {:else if activeTab === 'files'}
        <!-- Files Tab Content: all project files, including unit and room uploads -->
        <FilesPanel {projectId} emptyLabel="No project files uploaded" />
      {/if}

      <!-- Delete Unit Confirm Dialog -->
      <ConfirmDialog
        bind:open={showDeleteUnitConfirm}
        title="Delete Unit"
        message="Are you sure you want to delete this unit? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        onconfirm={handleDeleteUnit}
      />
    </div>
  </div>
</div>

<!-- Create Unit Modal -->
<Modal open={showCreateUnit} size="md" onclose={() => (showCreateUnit = false)}>
  {#snippet header()}
    <h3 class="text-lg font-medium text-text-primary mb-4">Create New Unit</h3>
  {/snippet}
  {#snippet children()}
    <div class="space-y-4">
      <div>
        <label for="unit-name" class="block text-sm font-medium text-text-secondary"
          >Unit Name</label
        >
        <input
          id="unit-name"
          type="text"
          bind:value={newUnit.name}
          placeholder="Enter unit name"
          class="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent-primary focus:border-accent-primary"
        />
      </div>

      <div>
        <label for="unit-notes" class="block text-sm font-medium text-text-secondary">Notes</label>
        <p class="mt-0.5 text-xs text-text-muted">
          Optional. Record what's specific to this shipment — variations, site conditions, handover
          details, etc.
        </p>
        <textarea
          id="unit-notes"
          bind:value={newUnit.notes}
          placeholder="e.g. Client requested dimmed corridor scenes; keypad engraving TBC"
          rows="3"
          class="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent-primary focus:border-accent-primary"
        ></textarea>
      </div>
    </div>
  {/snippet}
  {#snippet footer()}
    <div class="flex justify-end space-x-3">
      <button
        onclick={() => (showCreateUnit = false)}
        class="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-hover hover:bg-bg-hover rounded-md"
      >
        Cancel
      </button>
      <button
        onclick={createUnit}
        disabled={!newUnit.name.trim() || uiState.isLoading}
        class="px-4 py-2 text-sm font-medium text-accent-on-primary bg-accent-primary hover:opacity-90 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {#if uiState.isLoading}Creating...{:else}Create Unit{/if}
      </button>
    </div>
  {/snippet}
</Modal>

<!-- Clone Unit Modal -->
<Modal
  open={showCloneUnit}
  size="md"
  onclose={() => {
    showCloneUnit = false;
    unitToClone = null;
  }}
>
  {#snippet header()}
    <h3 class="text-lg font-medium text-text-primary mb-4">Clone Unit</h3>
  {/snippet}
  {#snippet children()}
    <div class="space-y-4">
      <div>
        <label for="clone-unit-name" class="block text-sm font-medium text-text-secondary"
          >New Unit Name</label
        >
        <input
          id="clone-unit-name"
          type="text"
          bind:value={cloneName}
          placeholder="Enter unit name"
          class="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent-primary focus:border-accent-primary"
        />
      </div>
    </div>
  {/snippet}
  {#snippet footer()}
    <div class="flex justify-end space-x-3">
      <button
        onclick={() => {
          showCloneUnit = false;
          unitToClone = null;
        }}
        class="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-hover hover:bg-bg-hover rounded-md"
      >
        Cancel
      </button>
      <button
        onclick={handleCloneUnit}
        disabled={!cloneName.trim() || cloning}
        class="px-4 py-2 text-sm font-medium text-accent-on-primary bg-accent-primary hover:opacity-90 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {#if cloning}Cloning...{:else}Clone Unit{/if}
      </button>
    </div>
  {/snippet}
</Modal>

<Modal open={!!proQuotePanel} size="md" onclose={() => (proQuotePanel = null)}>
  {#snippet header()}
    <h3 class="text-lg font-medium text-text-primary">ProQuote import details</h3>
  {/snippet}
  {#snippet children()}
    {#if proQuotePanel}
      <div class="space-y-4 text-sm">
        {#if proQuotePanel.warning}
          <p class="text-state-warn">{proQuotePanel.warning}</p>
        {/if}
        {#if proQuotePanel.unresolved?.length}
          <div>
            <p class="font-medium text-text-primary mb-1">Unresolved parts</p>
            <ul class="list-disc pl-5 text-text-secondary space-y-0.5">
              {#each proQuotePanel.unresolved as part}
                <li class="font-mono text-xs">{part}</li>
              {/each}
            </ul>
          </div>
        {/if}
        {#if proQuotePanel.created_products?.length}
          <div>
            <p class="font-medium text-text-primary mb-1">Auto-created products</p>
            <ul class="list-disc pl-5 text-text-secondary space-y-0.5">
              {#each proQuotePanel.created_products as part}
                <li class="font-mono text-xs">{part}</li>
              {/each}
            </ul>
          </div>
        {/if}
      </div>
    {/if}
  {/snippet}
  {#snippet footer()}
    <div class="flex justify-end">
      <button
        type="button"
        class="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-hover hover:bg-bg-hover rounded-md"
        onclick={() => (proQuotePanel = null)}
      >
        Close
      </button>
    </div>
  {/snippet}
</Modal>
